import * as React from 'react'
import { Plus } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { DatePicker } from '@/components/shared/date-picker'

export interface QuoteBuilderDetailsCardProps {
  /** DOM id used by the readiness checklist to focus this card. */
  sectionId: string
  statusBadge?: React.ReactNode
  recipientLabel: string
  templateSelect?: React.ReactNode
  title: string
  onTitleChange: (title: string) => void
  validUntil?: Date
  onValidUntilChange: (date: Date) => void
  /** Rendered in place once the description is revealed. */
  descriptionEditor: React.ReactNode
  hasDescription: boolean
  eventsSection: React.ReactNode
  requiresQuestionnaire: boolean
  onRequiresQuestionnaireChange: (checked: boolean) => void
  disabled?: boolean
}

export function QuoteBuilderDetailsCard({
  sectionId,
  statusBadge,
  recipientLabel,
  templateSelect,
  title,
  onTitleChange,
  validUntil,
  onValidUntilChange,
  descriptionEditor,
  hasDescription,
  eventsSection,
  requiresQuestionnaire,
  onRequiresQuestionnaireChange,
  disabled = false,
}: QuoteBuilderDetailsCardProps) {
  const [descriptionRevealed, setDescriptionRevealed] = React.useState(false)
  const showDescriptionEditor = hasDescription || descriptionRevealed

  return (
    <section
      id={sectionId}
      aria-labelledby={`${sectionId}-heading`}
      tabIndex={-1}
      className="scroll-mt-24 space-y-4 rounded-2xl border bg-card p-5 focus:outline-none"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 id={`${sectionId}-heading`} className="text-base font-semibold">Quote Details</h2>
          {statusBadge}
          <span className="text-sm text-muted-foreground">{recipientLabel}</span>
        </div>
        {templateSelect && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Template</span>
            {templateSelect}
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="quote-title">
            Title<span className="text-destructive"> *</span>
          </Label>
          <Input
            id="quote-title"
            name="title"
            autoComplete="off"
            value={title}
            disabled={disabled}
            required
            placeholder="Enter quote title…"
            onChange={(event) => onTitleChange(event.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="quote-valid-until">Valid Until</Label>
          <DatePicker
            value={validUntil}
            onChange={(date) => onValidUntilChange(date || new Date())}
            disabled={disabled}
          />
        </div>
      </div>

      {showDescriptionEditor ? (
        <div className="grid gap-2">
          <Label>Description</Label>
          {descriptionEditor}
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setDescriptionRevealed(true)}
          className="flex w-full items-center gap-2 rounded-xl border border-dashed px-4 py-3 text-left text-sm text-muted-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
        >
          <Plus className="size-4 shrink-0" aria-hidden="true" />
          Add a description — rich text, shown to the client under the title
        </button>
      )}

      <div id={`${sectionId}-events`}>{eventsSection}</div>

      <div className="flex items-start gap-3 border-t pt-4">
        <Switch
          id="quote-requires-questionnaire"
          checked={requiresQuestionnaire}
          onCheckedChange={onRequiresQuestionnaireChange}
          disabled={disabled}
        />
        <div>
          <Label htmlFor="quote-requires-questionnaire">
            Requires questionnaire
          </Label>
          <p className="text-sm text-muted-foreground">
            Ask the customer to complete the questionnaire when this quote is accepted
          </p>
        </div>
      </div>
    </section>
  )
}
